import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import PageContainer from "./containers/page-container";
import { useDispatch, useSelector } from "react-redux";
import type { ISpecialityResult } from "@/hooks/use-cost-estimator";
import {
  useCostEstimatorResultDownload,
  useProviderCostSearchInfinite,
} from "@/hooks/use-cost-estimator";
import useUserInfo from "@/hooks/use-user-info";
import type { DownloadOptions, SortOptions } from "@/constants";
import { COVERAGE_CODE, ModalIdentifiers, QueryKeys } from "@/constants";
import RightSidebar from "./components/right-sidebar";
import CostEstimatorDisclaimer from "./cost-estimator-disclaimer";
import CostEstimatorProviderInfo from "./cost-estimator-provider-info";
import SpendSummary from "./spend-summary";
import CostEstimatorResultSkeleton from "./cost-estimator-results-skeleton";
import CostEstimatorResultItem from "./components/cost-estimator/cost-estimator-result-item";
import useCostEstFilter from "@/hooks/use-cost-est-filter";
import CostEstimatorFilter from "./components/cost-estimator/cost-estimator-filter";
import CostEstimatorActions from "./components/cost-estimator/cost-estimator-actions";
import type { PlaceOfService, ProviderCost } from "@/model/provider-cost";
import {
  clearCostEstimatorFilter,
  setKey,
  setSort,
} from "@/store/price transparency/price-transparency-slice";
import { useSpendSummaryWithCoverage } from "@/hooks/use-spend-summary";
import type { PlaceMarker } from "@/model/place";
import MapView from "./components/provider-search/mapview";
import { useLocation } from "react-router-dom";
import { Hospital } from "lucide-react";
import { NoDataAvailable } from "@/pages/components/empty";

// import { usePDF } from "@react-pdf/renderer";
import { queryClient } from "@/hooks/use-query-client";
import type { RootState } from "@/store/store";

import { pdf } from "@react-pdf/renderer";
import PDFContent from "./components/cost-estimator/pdf-content";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PosLevelCostBreakdown from "./components/cost-estimator/pos-cost-breakdown";
import { benefitBreakdownSearch } from "@/services/provider-search-pt";
import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import type { BenefitBreakdownResults } from "@/types";
import { useFeatureName } from "@/hooks/use-pageTitle";
import ScreenRouteNames from "@/constants/screen-route-names";
import { emitEvent, EventConstants } from "@/lib/event-emitter";
import { ErrorNotificationType } from "./components/error-notification";

export default function CostEstimatorResults() {
  /**
   * State
   */
  const location = useLocation();
  const featureName = useFeatureName(ScreenRouteNames.COST_ESTIMATOR.path);
  // console.log("Guest Test Point: 2-> ", location.pathname)
  // const [instance, updateInstance] = usePDF();
  const [openMapView, setOpenMapView] = useState<{
    isMapViewOpen: boolean;
    index: number | null;
  }>({
    isMapViewOpen: false,
    index: null,
  });

  const [
    isCollapsedCostBreakdownContainerOpen,
    setIsCollapsedCostBreakdownContainerOpen,
  ] = useState(false);
  const [isPosCostBreakdownContainerOpen, setIsPosCostBreakdownContainerOpen] =
    useState(false);
  const [isProviderDescriptionOpen, setIsProviderDescriptionOpen] =
    useState(false);
  const [isSpendSummaryOpen, setIsSpendSummaryOpen] = useState(false);

  const [selectedProviderCost, setSelectedProviderCost] =
    useState<ProviderCost | null>(null);
  const [selectedPosItem, setSelectedPosItem] = useState<PlaceOfService | null>(
    null,
  );

  /**
   * Div Refrences
   */
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  /**
   * Hooks
   */
  const dispatch = useDispatch();
  const userInfo = useUserInfo();
  const costEstFilterParams = useCostEstFilter();
  const { coveragePlanDates } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );

  const { procedure } = useSelector(
    (state: RootState) => state.priceTransparency,
  );
  const specialities = queryClient.getQueryData([
    QueryKeys.SPECIALITIES,
    userInfo.username,
    procedure?.code,
  ]);

  const { accumulatorInfo } = useSpendSummaryWithCoverage(
    userInfo,
    userInfo?.planCoverageInfo.find(
      (coverage) => coverage?.claimCoverageCode === COVERAGE_CODE.MEDICAL,
    ),
    coveragePlanDates?.find(
      (coveragePlanDate) => coveragePlanDate.type === COVERAGE_CODE.MEDICAL,
    )?.periods?.[0],
  );

  const benefitBreakdownQueryData: UseQueryResult<
    BenefitBreakdownResults,
    Error
  > = useQuery({
    queryKey: [QueryKeys.BENEFIT_BREAKDOWN, userInfo.username],
    queryFn: async () => {
      const benefitBreakDownResponse = await benefitBreakdownSearch(userInfo);
      return benefitBreakDownResponse.results;
    },
    enabled: Boolean(userInfo?.username) && Boolean(userInfo?.accessToken),
  });

  const { mutate: mutateResultDownload, isPending: isDownloadInProgress } =
    useCostEstimatorResultDownload();

  const accumulator = useMemo(() => {
    return accumulatorInfo(userInfo.memberId, costEstFilterParams.tiers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costEstFilterParams.tiers]);

  // const providerCostQuery = useProviderCostSearch<ProviderCost>(
  //   userInfo,
  //   accumulator ?? [],
  // );
  // console.log("acc >>>>", spendSummaryWithCoverageQuery)

  const providerCostInfiniteQuery = useProviderCostSearchInfinite(
    userInfo,
    accumulator ?? [],
    costEstFilterParams,
    true,
  );

  /**
   * Component Lifecycle
   * Run on mount and when the sort option changes
   */
  // console.log(
  //   "#test: ",
  //   providerCostInfiniteQuery.isLoading,
  //   providerCostInfiniteQuery.isFetching,
  //   providerCostInfiniteQuery.isPending,
  // );
  useEffect(() => {
    // set the key for the cost estimator and this is required to differenciate
    // wether cost-estimator page is load ad a result of navigation page or navigation back
    // from this page

    dispatch(setKey(location.state.key));
    // console.log(
    //   "test: ",
    //   providerCostInfiniteQuery.isLoading,
    //   providerCostInfiniteQuery.isFetching,
    //   providerCostInfiniteQuery.isPending,
    // );
  }, [dispatch, location.state.key]);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries[0].isIntersecting;
        // console.log("## isVisible = ", {
        //   isVisible,
        //   next: providerCostInfiniteQuery.hasNextPage,
        //   fetch: providerCostInfiniteQuery.isFetching,
        //   loading: providerCostInfiniteQuery.isLoading,
        //   accumulator: accumulator.length,
        // });
        if (
          isVisible &&
          providerCostInfiniteQuery.hasNextPage &&
          !providerCostInfiniteQuery.isFetching &&
          !providerCostInfiniteQuery.isLoading &&
          accumulator.length > 0
        ) {
          handleSearch();
        }
      },
      { threshold: 0.5 },
    );
    const el = loadMoreRef.current;
    if (el) {
      observer.observe(el);
    }
    return () => {
      if (el) {
        observer.unobserve(el);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerCostInfiniteQuery?.hasNextPage]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accumulator, costEstFilterParams.sort]);

  /**
   * Handlers
   */

  const handleClear = () => {
    dispatch(
      clearCostEstimatorFilter((specialities as ISpecialityResult).default),
    );
  };

  const handleSearch = () => {
    if (accumulator.length > 0) {
      providerCostInfiniteQuery.fetchNextPage();
    }
  };

  const handleSortOption = (option: SortOptions) => {
    // console.log("## handleSortOption = ", option);
    dispatch(setSort(option));
  };

  const handleDownloadOption = async (option: DownloadOptions) => {
    mutateResultDownload(
      {
        userInfo,
        accumulators: accumulator,
        filter: { ...costEstFilterParams, limit: option },
        pageParam: 0,
      },
      {
        onSuccess: async (result) => {
          const blob = await pdf(
            <PDFContent
              userInfo={userInfo}
              costEstFilterParams={{ ...costEstFilterParams, limit: option }}
              providerCost={result?.data as ProviderCost[]}
            />,
          ).toBlob();
          const pdfURL = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = pdfURL;
          link.setAttribute("download", `Provider Search Pricing Result.pdf`);
          document.body.appendChild(link);
          link.click();
          link?.parentNode?.removeChild(link);
        },
        onError: (error) => {
          emitEvent(EventConstants.ADD_ERROR, {
            message:
              error.message ??
              "No results found for download, Please try again.",
            type: ErrorNotificationType.ERROR,
          });
        },
      },
    );
  };

  const handleModal = (
    identifier: ModalIdentifiers,
    providerCost?: ProviderCost,
    posItem?: PlaceOfService,
  ) => {
    // console.log("## handleModal providerCost= ", identifier, providerCost);
    // console.log("## handleModal pos= ", posItem);
    setSelectedProviderCost(providerCost ?? null);
    setSelectedPosItem(posItem ?? null);

    switch (identifier) {
      case ModalIdentifiers.PROVIDER_INFO:
        setIsProviderDescriptionOpen(true);
        break;
      case ModalIdentifiers.COLLAPSED_COST_BREAKDOWN_CONTAINER:
        setIsCollapsedCostBreakdownContainerOpen(true);
        break;
      case ModalIdentifiers.POS_COST_BREAKDOWN_CONTAINER:
        setIsPosCostBreakdownContainerOpen(true);
        break;
      case ModalIdentifiers.SPEND_SUMMARY:
        setIsSpendSummaryOpen(true);
        break;
      default:
        break;
    }
  };

  const openSelectedProviderInMapview = (index: number | null) => {
    setOpenMapView({
      isMapViewOpen: true,
      index,
    });
  };

  const getMarkers = (index: number | null) => {
    if (providerCostInfiniteQuery.data?.pages) {
      const allMarkers: PlaceMarker[] = providerCostInfiniteQuery.data?.pages
        .flatMap((page) => page.data)
        .map((obj: ProviderCost) => {
          const marker: PlaceMarker = {
            name: obj.provider?.fullName ?? "",
            address: obj.address,
            distance: obj.distance,
            pinSymbol: "Pin",
            color: "green",
            lat: obj.lat,
            lng: obj.lon,
          };
          return marker;
        });

      if (index == null) {
        return allMarkers;
      } else {
        const marker = allMarkers?.length > 0 ? allMarkers[index] : [];
        return [marker];
      }
    } else {
      return [];
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <PageContainer title={`${featureName} Results`} enableBackButton={true}>
        <CostEstimatorFilter onSearch={handleSearch} onClear={handleClear} />

        {Boolean(providerCostInfiniteQuery.data?.pages) && (
          <div className="flex flex-col gap-2">
            {
              // pages array exists
              providerCostInfiniteQuery.data.pages.length === 0 ||
              // pages = [{}] — only one empty object page
              (providerCostInfiniteQuery.data.pages.length === 1 &&
                Object.keys(providerCostInfiniteQuery.data.pages[0] ?? {})
                  .length === 0) ||
              // pages exist but no data when flattened
              !providerCostInfiniteQuery.data.pages.flatMap(
                (page) => page?.data ?? [],
              ).length ? (
                <NoDataAvailable
                  message="Sorry, we couldn't find any providers"
                  icon={Hospital}
                />
              ) : (
                <>
                  <CostEstimatorActions
                    onDownload={handleDownloadOption}
                    onSort={handleSortOption}
                    onMapView={openSelectedProviderInMapview}
                    showDownloadLoader={isDownloadInProgress}
                  />
                  {Boolean(providerCostInfiniteQuery.data.pages) &&
                    providerCostInfiniteQuery.data.pages
                      .flatMap((page) => page.data)
                      .map((provider: ProviderCost, index: number) => (
                        <CostEstimatorResultItem
                          key={index}
                          index={index}
                          open={index === 0 ? true : false}
                          value={provider}
                          onButtonClick={handleModal}
                          onMapView={openSelectedProviderInMapview}
                          deductibleStatus={
                            accumulator[0]?.deductibleStatus?.hasMet
                          }
                        />
                      ))}
                </>
              )
            }
          </div>
        )}

        {accumulator?.length === 0 && (
          <div className="mt-2 flex flex-col gap-2">
            <NoDataAvailable
              title="Accumulator Data Unavailable"
              message="Sorry, Accumulator Not Found"
            />
          </div>
        )}

        {(providerCostInfiniteQuery.isFetching ||
          providerCostInfiniteQuery.isLoading ||
          providerCostInfiniteQuery.isPending) &&
          accumulator.length > 0 && (
            <div className="mt-2 flex flex-col gap-2">
              <CostEstimatorResultSkeleton />
              <CostEstimatorResultSkeleton />
              <CostEstimatorResultSkeleton />
              <CostEstimatorResultSkeleton />
              <CostEstimatorResultSkeleton />
              <CostEstimatorResultSkeleton />
            </div>
          )}

        {/* Load More */}
        {Boolean(providerCostInfiniteQuery.hasNextPage) && (
          <div ref={loadMoreRef} className="mt-2 h-8" />
        )}

        {/* Collapsed View Costbreakdown */}
        <RightSidebar
          title="Disclaimer"
          isOpen={isCollapsedCostBreakdownContainerOpen}
          onClose={() => setIsCollapsedCostBreakdownContainerOpen(false)}
        >
          <Tabs defaultValue="disclaimer" className="tabs">
            <div className="tabs-list-container flex flex-col gap-y-4">
              <TabsList className="tabs-list">
                <TabsTrigger className="tabs-trigger" value="disclaimer">
                  Disclaimer
                </TabsTrigger>
                {/* <TabsTrigger className="tabs-trigger" value="cost-calculation">
                  Cost Calculation
                </TabsTrigger> */}
              </TabsList>
            </div>

            <TabsContent value="disclaimer" className="tabs-content">
              <CostEstimatorDisclaimer
                planName={userInfo.planId}
                serviceProvider={userInfo.serviceProvider}
                supportNumber={userInfo.supportNumber}
              />
            </TabsContent>
            {/* <TabsContent value="cost-calculation" className="tabs-content">
              <CollapsedCostBreakdown
                userInfo={userInfo}
                providerCost={selectedProviderCost}
                spendSummaryAccData={accumulator}
                benefitBreakdownQueryData={benefitBreakdownQueryData}
              />
            </TabsContent> */}
          </Tabs>
        </RightSidebar>

        {/* Expanded View Costbreakdown */}
        <RightSidebar
          title={selectedPosItem?.type.split("_").join(" ") ?? "Disclaimer"}
          isOpen={isPosCostBreakdownContainerOpen}
          onClose={() => setIsPosCostBreakdownContainerOpen(false)}
        >
          <Tabs defaultValue="disclaimer" className="tabs">
            <div className="tabs-list-container flex flex-col gap-y-4">
              <TabsList className="tabs-list">
                <TabsTrigger className="tabs-trigger" value="disclaimer">
                  Disclaimer
                </TabsTrigger>
                <TabsTrigger className="tabs-trigger" value="cost-calculation">
                  Cost Calculation
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="disclaimer" className="tabs-content">
              <CostEstimatorDisclaimer
                planName={userInfo.planId}
                serviceProvider={userInfo.serviceProvider}
                supportNumber={userInfo.supportNumber}
              />
            </TabsContent>
            <TabsContent value="cost-calculation" className="tabs-content">
              <PosLevelCostBreakdown
                posItem={selectedPosItem!}
                providerCost={selectedProviderCost!}
                userInfo={userInfo}
                benefitBreakdownQueryData={benefitBreakdownQueryData}
                spendSummaryAccData={accumulator}
              />
            </TabsContent>
          </Tabs>
        </RightSidebar>

        {/* Provider Info */}
        <RightSidebar
          title="Provider Info"
          isOpen={isProviderDescriptionOpen}
          onClose={() => setIsProviderDescriptionOpen(false)}
        >
          {Boolean(selectedProviderCost) && (
            <CostEstimatorProviderInfo providerCost={selectedProviderCost} />
          )}
        </RightSidebar>

        {/* Spend Summary */}
        <RightSidebar
          title="Spend Summary"
          isOpen={isSpendSummaryOpen}
          onClose={() => setIsSpendSummaryOpen(false)}
        >
          <Suspense fallback={<div>Loading...</div>}>
            <SpendSummary shouldWrap={false} useMedCoverage />
          </Suspense>
        </RightSidebar>

        {/* Map View */}
        <RightSidebar
          title="Map View"
          isOpen={openMapView.isMapViewOpen}
          onClose={() =>
            setOpenMapView({
              isMapViewOpen: false,
              index: null,
            })
          }
        >
          {providerCostInfiniteQuery.data?.pages &&
          openMapView.isMapViewOpen ? (
            <MapView
              searchResult={getMarkers(openMapView.index) as PlaceMarker[]}
            />
          ) : null}
        </RightSidebar>
      </PageContainer>
    </div>
  );
}
